import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  fetchCarHouseUsers,
  addCarHouseUser,
  editCarHouseUser,
  removeCarHouseUser,
} from '../../store/actions/carHouseUser-actions';

import SettingsModal from './SettingsModal';

import CardItem from '../UI/CardItem/CardItem';
import ModalForm from '../UI/Modal/Modal-form/ModalForm';
import Spinner from '../UI/Spinner/Spinner';
import PageHeader from '../UI/PageHeader/PageHeader';

import { List, message, Input, Pagination, Space } from 'antd';

import { fetchCarHouseLogos } from '../../store/actions/carHouseLogos-actions';

const { Search } = Input;

const CarHouseUsers = () => {
  const { t } = useTranslation();
  const { isLoading, carHouseUsers, count } = useSelector(
    (state) => state.carHouseUser
  );
  const loggedInUser = useSelector((state) => state.auth.loggedInUser);
  const { carhouseId } = useParams();
  const dispatch = useDispatch();
  const [selectedCarHouseUser, setSelectedCarHouseUser] = useState(null);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(8);
  const [params, setParams] = useState(null);
  const [pagination, setPagination] = useState({ page: page, total: perPage });

  let carHouseId =
    loggedInUser.role.name === 'carHouseAdmin'
      ? loggedInUser.carHouseId
      : carhouseId;

  const [openForm, setOpenForm] = useState(false);

  const getCarHouseUsers = useCallback(
    (pagination, options) => {
      dispatch(
        fetchCarHouseUsers({
          carHouseId: carHouseId,
          pagination,
          options,
        })
      );
    },
    [dispatch, carHouseId]
  );

  useEffect(() => {
    setPagination({ page: page, total: perPage });

    const controler = new AbortController();
    const { signal } = controler;

    const options = {
      params,
      signal,
    };

    getCarHouseUsers(pagination, options);

    dispatch(
        fetchCarHouseLogos({
          carHouseId: carHouseId,
        })
      );

    return () => controler.abort();
  }, [carHouseId, params, page]);

  const onAddUser = () => {
    setSelectedCarHouseUser(null);
    setOpenForm(true);
  };

  const onEditUser = (carHouseUser) => {
    setSelectedCarHouseUser(carHouseUser);
    setOpenForm(true);
  };

  const deleteCarHouseUser = async (id) => {
    try {
      await dispatch(
        removeCarHouseUser({ carHouseId: carhouseId, userId: id })
      );
      message.success(t('carHouseUsers.userRemoved'));
    } catch (err) {
      console.log(err);
      message.error(t('carHouseUsers.somethingWrong'));
    }
  };

  const addCarHouseUserHandler = async (body) => {
    try {
      const resultAction = await dispatch(
        addCarHouseUser({ carHouseId: carHouseId, body })
      );

      if (addCarHouseUser.fulfilled.match(resultAction)) {
        message.success(t('carHouseUsers.userAdded'));
        setOpenForm(false);
      } else {
        const error = resultAction.payload || resultAction.error;
        const errorMessage =
          error?.errorCode === 4090001
            ? t('carHouseUsers.userExists')
            : error?.description || t('carHouseUsers.somethingWrong');
        message.error(errorMessage);
      }
    } catch (err) {
      message.error('Unexpected error occurred');
    }
  };

  const editCarHouseUserHandler = async (body) => {
    try {
      const resultAction = await dispatch(
        editCarHouseUser({
          carHouseId: carHouseId,
          userId: selectedCarHouseUser.id,
          body,
        })
      );

      if (editCarHouseUser.fulfilled.match(resultAction)) {
        message.success(t('carHouseUsers.userUpdated'));
        setOpenForm(false);
      } else {
        const error = resultAction.payload || resultAction.error;
        const errorMessage = error?.description || t('carHouseUsers.somethingWrong');
        message.error(errorMessage);
      }
    } catch (err) {
      message.error(t('common.unexpectedError'));
    }
  };

  const onSearch = (value) => {
    let data = {
      search: value,
    };
    value === '' ? setParams(null) : setParams(data);
    setPage(1);
    setPagination({ page: 1, total: 1 });
  };

  const changePageHandler = (pageNumber) => {
    setPage(pageNumber);
    setPagination({ page: pageNumber, total: 1 });
  };

  const onSettings = (carHouseUser) => {
    setSelectedCarHouseUser(carHouseUser);
    setOpenSettingsModal(true);
  };

  const onAssignLogo = () => {
   const controler = new AbortController();
    const { signal } = controler;

    const options = {
      params,
      signal,
    };

    getCarHouseUsers(pagination, options);
  };

  let content;
  if (isLoading) {
    content = <Spinner />;
  } else {
    content = (
      <div className="container-fluid p-0">
        <List
          grid={{
            gutter: 16,
            xs: 1,
            sm: 1,
            md: 2,
            lg: 3,
            xl: 3,
            xxl: 4,
          }}
          dataSource={carHouseUsers}
          renderItem={(item) => (
            <CardItem
              user={item}
              onEdit={onEditUser}
              onDelete={deleteCarHouseUser}
              onSettings={onSettings}
            />
          )}
        />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('carHouseUsers.title')}
        onAdd={onAddUser}
        buttonText={t('carHouseUsers.addUser')}
        showButton
      />
      <Space
        direction="vertical"
        size="large"
        align="center"
        style={{
          display: 'flex',
          padding: '20px 0px 40px',
        }}
      >
        <Search
          placeholder={t('carHouseUsers.searchUser')}
          onSearch={onSearch}
          align="middle"
          allowClear
          style={{
            width: '100%',
            display: 'block',
          }}
        />
      </Space>
      {content}
      <Pagination
        defaultCurrent={page}
        total={count / perPage}
        pageSize={1}
        onChange={changePageHandler}
      />

      <ModalForm
        open={openForm}
        selectedItem={selectedCarHouseUser}
        add={addCarHouseUserHandler}
        edit={editCarHouseUserHandler}
        title={t('carHouseUsers.title')}
        onClose={() => {
          setOpenForm(false);
        }}
      />

      <SettingsModal
        visible={openSettingsModal}
        onClose={() => setOpenSettingsModal(false)}
        user={selectedCarHouseUser}
        carHouseId={carhouseId}
        onAssignLogo={onAssignLogo}
      />
    </>
  );
};

export default CarHouseUsers;
